import { Box, Grid } from "@mui/material";
import React, { useEffect, useRef, useState } from "react";
import { AppUtils } from "source/framework/appUtils";
import { SaveDataObj } from "source/framework/saveData";
import { ApiSuccess } from "ui/Scripts/source/models/common/apiSuccess";
import { ConfirmationDialogResult, EnumHelper, RiskAssesmentFrequency, RiskType } from "ui/Scripts/source/models/enums";
import { Translations } from "ui/Scripts/source/models/translations";
import * as store from "source/framework/customStore";
import * as riskService from "source/models/services/riskService";
import { ISessionState } from "source/models/session/sessionState";
import * as storeActions from "source/models/store/storeActions";
import { DialogButtons } from "source/components/framework/Dialog/DialogButtons";
import { DialogItemsNav } from "source/components/framework/Dialog/DialogItemsNav";
import { DialogSectionCard } from "source/components/framework/Dialog/DialogSectionCard";
import { SaveSuccess } from "ui/Scripts/source/models/common/saveSuccess";
import { Base } from "ui/Scripts/source/framework/base";
import { IdTitleNode, IIdTitleNode } from "ui/Scripts/source/models/common/idTitleNode";
import { removeConfirmationWord } from "ui/Scripts/source/models/consts";
import { UnitBlocks } from "source/components/unit/UnitBlocks";
import { IRecordUnit, RecordUnit } from "ui/Scripts/source/models/unit/recordUnit";
import { IRiskTemplateEditItem } from "ui/Scripts/source/models/riskTemplate/riskTemplateEditItem";
import { Dialog } from "source/components/framework/Dialog/Dialog";
import { GridRow } from "source/components/framework/Grid/GridRow";
import { FormField } from "ui/Scripts/source/components/framework/FormField";
import { FormCheckBox } from "source/components/framework/FormCheckBox";
import { FormText } from "source/components/framework/FormText";
import { IdTitleDropdown } from "ui/Scripts/source/components/framework/Dropdown/IdTitleDropdown";
import { TreeCheckboxDropdown } from "ui/Scripts/source/components/framework/Dropdown/TreeCheckboxDropdown";

const riskAssesmentFrequencyOptions = [
    { id: RiskAssesmentFrequency.Continuous.toString(), title: EnumHelper.RiskAssesmentFrequencyToStr(RiskAssesmentFrequency.Continuous) },
    { id: RiskAssesmentFrequency.Monthly.toString(), title: EnumHelper.RiskAssesmentFrequencyToStr(RiskAssesmentFrequency.Monthly) },
    { id: RiskAssesmentFrequency.Quarterly.toString(), title: EnumHelper.RiskAssesmentFrequencyToStr(RiskAssesmentFrequency.Quarterly) },
    { id: RiskAssesmentFrequency.HalfYearly.toString(), title: EnumHelper.RiskAssesmentFrequencyToStr(RiskAssesmentFrequency.HalfYearly) },
    { id: RiskAssesmentFrequency.Yearly.toString(), title: EnumHelper.RiskAssesmentFrequencyToStr(RiskAssesmentFrequency.Yearly) }
];

interface IRiskTemplateDialogProps {
    id: string;
    sessionState: ISessionState;
    companyId: string;
    hasPrevious: boolean;
    hasNext: boolean;
    addToCompany: boolean;
    onPrevious: () => void;
    onNext: () => void;
    onSaved: (id: string) => void;
    onClose: () => void;
    onRemove: (ids: string[]) => Promise<ApiSuccess>;
    onRefreshList: () => Promise<void>;
}

export interface IRiskTemplateDialogState {
    riskCategoryId: string;
    riskType: RiskType;
    code: string;
    description: string;
    threat: string;
    controls: string;
    owner: string;
    assesmentFrequency: RiskAssesmentFrequency;
    activeState: number;
    opportunity: boolean;
    opportunityBenefit: string;
    opportunityDescription: string;
    item: IRiskTemplateEditItem;
    rowId: string;
    riskUnits: IdTitleNode[];
    riskTemplateUnits: IRecordUnit[];
    riskCategories: IIdTitleNode[];
}

export const RiskTemplateDialog = (props: IRiskTemplateDialogProps) => {
    const orgStateHash = useRef<string>("");

    const [state, setState] = useState<IRiskTemplateDialogState>({
        item: null,
        riskCategoryId: "",
        riskType: RiskType.Risk,
        code: "",
        description: "",
        threat: "",
        controls: "",
        owner: "",
        assesmentFrequency: RiskAssesmentFrequency.Continuous,
        activeState: 0,
        opportunity: false,
        opportunityBenefit: "",
        opportunityDescription: "",
        rowId: "",
        riskUnits: [],
        riskTemplateUnits: [],
        riskCategories: [],
    });

    const getSaveDataFromState = (state: IRiskTemplateDialogState): SaveDataObj => {
        const result = new SaveDataObj();
        result.append("id", state.item?.id);
        result.append("companyId", props.companyId);
        result.append("rowId", state.rowId);
        result.append("riskCategoryId", state.riskCategoryId);
        result.append("riskType", state.riskType);
        result.append("code", state.code);
        result.append("description", state.description);
        result.append("threat", state.threat);
        result.append("controls", state.controls);
        result.append("owner", state.owner);
        result.append("assesmentFrequency", state.assesmentFrequency);
        result.append("activeState", state.activeState);
        result.append("opportunity", state.opportunity);
        result.append("opportunityBenefit", state.opportunityBenefit);
        result.append("opportunityDescription", state.opportunityDescription);
        const isNew = state.item?.isNew() || false;
        result.append("unitIds", isNew
            ? state.riskTemplateUnits.map(i => {
                return i.unitId;
            })
            : null);
        return result;
    };

    const checkErrors = (): Promise<boolean> => {
        return new Promise<boolean>((resolve) => {
            if (!state.code) {
                store.customStore.dispatch(storeActions.showErrorMessage(Translations.NameMustBeDefined));
                return resolve(false);
            }
            if (!state.owner) {
                store.customStore.dispatch(storeActions.showErrorMessage(Translations.RiskOwnerMustBeDefined));
                return resolve(false);
            }
            return resolve(true);
        });
    };

    const loadData = async (id: string, addToCompany: boolean) => {
        const result = await AppUtils.callService(() => riskService.getRiskTemplateEdit(props.companyId, id, addToCompany), false);
        if (!result) {
            if (!state.item) {
                props.onClose();
            }
            return;
        }
        const item = result.item;
        const stateChange = {
            item,
            riskCategoryId: item.riskCategoryId,
            riskType: item.riskType,
            code: item.code,
            description: item.description,
            threat: item.threat,
            controls: item.controls,
            owner: item.owner,
            assesmentFrequency: item.assesmentFrequency,
            activeState: item.activeState,
            opportunity: item.opportunity,
            opportunityBenefit: item.opportunityBenefit,
            opportunityDescription: item.opportunityDescription,
            rowId: item.rowId,
            riskUnits: result.units,
            riskTemplateUnits: result.riskTemplateUnits,
            riskCategories: result.riskCategories,
        };
        setState((old) => {
            const newState = { ...old, ...stateChange };
            orgStateHash.current = getSaveDataFromState(newState).hash;
            return newState;
        });
    };

    useEffect(() => {
        if (!props.id) return;
        loadData(props.id, props.addToCompany);
    }, [props.companyId, props.id, props.addToCompany]);

    const saveToDb = async (): Promise<SaveSuccess> => {
        if (!await AppUtils.validate(checkErrors)) return null;
        const saveData = getSaveDataFromState(state);
        //Call server
        const result = await AppUtils.callService(() => riskService.saveRiskTemplateEdit(saveData.obj));
        if (!result) return null;
        //Update state and hash
        const stateChange = { id: result.id, rowId: result.rowId };
        setState(old => {
            const newState = { ...old, ...stateChange };
            orgStateHash.current = getSaveDataFromState(newState).hash;
            return newState;
        });
        return result;
    };

    const handleSave = async (closeDialog: boolean) => {
        const result = await saveToDb();
        if (!result) return;
        if (!closeDialog) {
            loadData(result.id, false);
            props.onSaved(result.id);
            return;
        }
        props.onClose();
    };

    const handleRemove = async () => {
        if (state.item?.isNew()) return;
        if (await AppUtils.showConfirmationDialog(String.format(Translations.RiskTemplateRemoveConfirmation, state.item.code), false, removeConfirmationWord) === ConfirmationDialogResult.Yes) {
            // Call server
            const result = await props.onRemove([state.item?.id]);
            if (!result) return;
            props.onClose();
        }
    };

    const handleCancel = async () => {
        const cancelResult = await AppUtils.cancel(getSaveDataFromState(state).hash, orgStateHash.current, null);
        if (cancelResult === ConfirmationDialogResult.Cancel) return;
        if (cancelResult === ConfirmationDialogResult.Yes) {
            handleSave(true);
        } else {
            props.onClose();
        }
    };

    const canMoveOut = async (): Promise<boolean> => {
        return await AppUtils.canMoveOut(getSaveDataFromState(state).hash, orgStateHash.current,
            async () => !!(await saveToDb()),
            props.onRefreshList,
            (newStateHash) => { orgStateHash.current = newStateHash; });
    };

    const handlePrevious = async () => {
        if (!await canMoveOut()) return;
        setState((old) => ({
            ...old,
            formSelectedField: null
        }));
        props.onPrevious();
    };

    const handleNext = async () => {
        if (!await canMoveOut()) return;
        setState((old) => ({
            ...old,
            formSelectedField: null
        }));
        props.onNext();
    };

    const isNew = state.item?.isNew();

    // #region RiskTemplateUnits
    const addUnits = async (units: IIdTitleNode[]): Promise<ApiSuccess> => {
        const addUnits = units.filter(i => !state.riskTemplateUnits.find(j => j.unitId === i.id));
        if (!addUnits || addUnits.length < 1) return null;
        if (!isNew) {
            const result = await AppUtils.callService(() => riskService.riskTemplateAddUnits(props.companyId, state.item.id, addUnits.map(i => i.id)), true);
            if (!result) return null;
        }
        const riskTemplateUnits = state.riskTemplateUnits.slice(0);
        for (const unit of addUnits) {
            const recordUnit = new RecordUnit();
            recordUnit.id = Base.getGuid();
            recordUnit.unitId = unit.id;
            recordUnit.title = unit.title;
            riskTemplateUnits.push(recordUnit);
        }
        setState((old) => ({ ...old, riskTemplateUnits }));
        return new ApiSuccess();
    };

    const handleAddUnit = (unit: IIdTitleNode): Promise<ApiSuccess> => {
        if (!unit) return;
        return addUnits([unit]);
    };

    const handleRemoveUnits = async (unitIds: string[]): Promise<ApiSuccess> => {
        if (!unitIds || unitIds.length < 1) return;
        if (!isNew) {
            const result = await AppUtils.callService(() => riskService.riskTemplateRemoveUnits(props.companyId, state.item.id, unitIds));
            if (!result) return null;
        }
        setState((old) => ({ ...old, riskTemplateUnits: old.riskTemplateUnits.filter(i => unitIds.indexOf(i.unitId) < 0) }));
        return new ApiSuccess();
    };
    // #endregion RiskTemplateUnits

    if (!state.item) return null;

    return <Dialog
        id="riskTemplateDialog"
        maxWidth="xl"
        maxHeight="100%"
        title={isNew ? Translations.AddRiskTemplate : (Translations.RiskTemplate + ": " + state.item?.code)}
        body={<Box display="flex" flexDirection="column" height="100%">
            <Grid container height="100%">
                <Grid item sm={props.companyId ? 8 : 12} height="100%">
                    <DialogSectionCard headerTitle={Translations.BasicInformation} height="auto">
                        <GridRow container>
                            <Grid item sm={10}>
                                <FormField title={Translations.Name} fullWidth required margin="0">
                                    <FormText
                                        autoFocus={isNew}
                                        maxLength={100}
                                        required={true}
                                        tooltip={Translations.Name}
                                        value={state.code}
                                        onChange={(code) => setState((old) => ({ ...old, code }))}
                                    />
                                </FormField>
                            </Grid>
                            <Grid item sm={2}>
                                <FormField fullWidth title={<span>&nbsp;</span>} margin="0">
                                    <FormCheckBox title={Translations.InUse} checked={state.activeState > 0} onChange={(checked: boolean) => setState((old) => ({ ...old, activeState: checked ? 1 : 0 }))} />
                                </FormField>
                            </Grid>
                        </GridRow>
                        <GridRow>
                            <Grid item sm={3}>
                                <FormField title={Translations.AType}>
                                    <IdTitleDropdown
                                        selectedId={state.riskType.toString()}
                                        disabled
                                        items={[
                                            { id: RiskType.Risk.toString(), title: Translations.Risk },
                                            { id: RiskType.Opportunity.toString(), title: Translations.Opportunity }
                                        ]}
                                        onChange={(riskType: string) => setState((old) => ({ ...old, riskType: riskType.toInteger() }))}
                                    />
                                </FormField>
                            </Grid>
                            <Grid item sm={3}>
                                <FormField title={Translations.RiskCategory}>
                                    <TreeCheckboxDropdown
                                        selectedIds={[state.riskCategoryId]}
                                        singleSelect
                                        values={state.riskCategories}
                                        onChange={(riskCategoryIds: string[]) => setState((old) => ({ ...old, riskCategoryId: riskCategoryIds.length ? riskCategoryIds[0] : old.riskCategoryId }))}
                                    />
                                </FormField>
                            </Grid>
                            <Grid item sm={3}>
                                <FormField title={Translations.RiskOwner} fullWidth required>
                                    <FormText
                                        maxLength={100}
                                        required={true}
                                        tooltip={Translations.RiskOwner}
                                        value={state.owner}
                                        onChange={(owner) => setState((old) => ({ ...old, owner }))}
                                    />
                                </FormField>
                            </Grid>
                            <Grid item sm={3}>
                                <FormField title={Translations.RiskAssesmentFrequency}>
                                    <IdTitleDropdown
                                        selectedId={state.assesmentFrequency.toString()}
                                        items={riskAssesmentFrequencyOptions}
                                        onChange={(assesmentFrequency: string) => setState((old) => ({ ...old, assesmentFrequency: assesmentFrequency.toInteger() }))}
                                    />
                                </FormField>
                            </Grid>
                        </GridRow>
                        <GridRow>
                            <Grid item sm={12}>
                                <FormField title={Translations.Description} fullWidth>
                                    <FormText
                                        maxLength={1000}
                                        tooltip={Translations.Description}
                                        value={state.description}
                                        multiline
                                        rows={6}
                                        onChange={(description) => setState((old) => ({ ...old, description }))}
                                    />
                                </FormField>
                            </Grid>
                        </GridRow>
                        <GridRow>
                            <Grid item sm={6}>
                                <FormField title={Translations.RiskThreat} fullWidth>
                                    <FormText
                                        maxLength={1000}
                                        tooltip={Translations.RiskThreat}
                                        value={state.threat}
                                        multiline
                                        rows={6}
                                        onChange={(threat) => setState((old) => ({ ...old, threat }))}
                                    />
                                </FormField>
                            </Grid>
                            <Grid item sm={6}>
                                <FormField title={Translations.RiskControls} fullWidth>
                                    <FormText
                                        maxLength={1000}
                                        tooltip={Translations.RiskControls}
                                        value={state.controls}
                                        multiline
                                        rows={6}
                                        onChange={(treatments) => setState((old) => ({ ...old, controls: treatments }))}
                                    />
                                </FormField>
                            </Grid>
                        </GridRow>
                    </DialogSectionCard>
                    <DialogSectionCard headerTitle={Translations.Opportunity} height="auto" margin="15px 0 0 0">
                        <GridRow>
                            <Grid item sm={12}>
                                <FormCheckBox
                                    title={Translations.RiskContainsOpportunity}
                                    checked={state.opportunity}
                                    onChange={opportunity => setState((old) => ({ ...old, opportunity, opportunityBenefit: "", opportunityDescription: "" }))}
                                />
                            </Grid>
                        </GridRow>
                        {state.opportunity &&
                            <>
                                <GridRow>
                                    <Grid item sm={12}>
                                        <FormField title={Translations.OpportunityBenefit} fullWidth>
                                            <FormText
                                                maxLength={1000}
                                                placeholder={Translations.OpportunityBenefitPlaceholder}
                                                tooltip={Translations.OpportunityBenefit}
                                                value={state.opportunityBenefit}
                                                multiline
                                                rows={6}
                                                onChange={(opportunityBenefit) => setState((old) => ({ ...old, opportunityBenefit }))}
                                            />
                                        </FormField>
                                    </Grid>
                                </GridRow>
                                <GridRow>
                                    <Grid item sm={12}>
                                        <FormField title={Translations.OpportunityDescription} fullWidth>
                                            <FormText
                                                maxLength={1000}
                                                placeholder={Translations.OpportunityDescriptionPlaceholder}
                                                tooltip={Translations.OpportunityDescription}
                                                value={state.opportunityDescription}
                                                multiline
                                                rows={6}
                                                onChange={(opportunityDescription) => setState((old) => ({ ...old, opportunityDescription }))}
                                            />
                                        </FormField>
                                    </Grid>
                                </GridRow>
                            </>
                        }
                    </DialogSectionCard>
                </Grid>
                {!!props.companyId &&
                    <Grid item sm={4} sx={{ paddingLeft: "16px" }} height="100%">
                        <UnitBlocks
                            title={Translations.RiskTemplateUnits}
                            noUnitsMessage={Translations.RiskTemplateUsedInAllUnits}
                            showInPanel={true}
                            height="100%"
                            unitTree={state.riskUnits}
                            units={state.riskTemplateUnits.map(i => IdTitleNode.createIdTitleNode(i.unitId, i.title, []))}
                            onAddUnit={handleAddUnit}
                            onRemoveUnits={handleRemoveUnits}
                        />
                    </Grid>
                }
            </Grid>
        </Box>
        }
        actions={
            <Box display="flex" justifyContent="space-between" width="100%">
                <DialogItemsNav
                    modifyUser={!isNew ? state.item?.modifyUser : null}
                    hasPrevious={props.hasPrevious}
                    hasNext={props.hasNext}
                    onPrevious={handlePrevious}
                    onNext={handleNext}
                />
                <DialogButtons
                    isNew={isNew}
                    onCancel={handleCancel}
                    onRemove={!isNew ? handleRemove : null}
                    onSave={() => handleSave(false)}
                />
            </Box>
        }
        onClose={handleCancel}
    />;
};
